/**
 * File Upload API Client
 *
 * Handles file uploads to the platform's storage service.
 * Files are stored in the configured storage (local, S3, GCS, Azure)
 * and metadata is persisted with a UUID for retrieval.
 *
 * Usage:
 * ```typescript
 * // Upload a file
 * const result = await uploadFile(file, 'model_chat', sessionId);
 *
 * // Get file URL for display
 * const url = getFileUrl(result.file_id);
 *
 * // Get file info
 * const info = await getFileInfo(result.file_id);
 * ```
 */

import { config } from '../config'
import { authenticatedRequestHeaders } from './api/core'
import { createServiceFetch, sanitizeFetchUrl } from '../utils/validateFetchUrl'
import { logger } from '../utils/logger'
import { safeRegexTest } from '../utils/safeRegex'

const fileApiFetch = createServiceFetch(config.api.baseUrl)

/** Auth headers for file storage. Includes the platform-admin selected tenant. */
function fileAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem(config.auth.tokenKey)
  return authenticatedRequestHeaders(token)
}

function applyFileAuthHeaders(xhr: XMLHttpRequest): void {
  const headers = fileAuthHeaders()
  for (const [name, value] of Object.entries(headers)) {
    xhr.setRequestHeader(name, value)
  }
}

export interface UploadedFile {
  /** Unique file identifier (UUID) */
  file_id: string

  /** Path in storage */
  storage_path: string

  /** Original filename */
  original_name: string

  /** MIME type */
  content_type: string

  /** File size in bytes */
  size_bytes: number

  /** URL for accessing the file */
  url: string
}

export interface FileInfo {
  file_id: string
  storage_type: string
  storage_path: string
  original_name: string
  content_type: string
  size_bytes: number
  context: string
  session_id: string | null
  created_at: number
  /** Set for workflow_ingestion folder uploads (path under selected folder). */
  relative_path?: string | null
}

export type StorageContext =
  | 'prompt_chat'
  | 'model_chat'
  | 'agent_playground'
  | 'workflow_ingestion'
  | 'migration_etl_code'
  | 'migration_object_catalog'
  | 'migration_shell_cron'
  | 'migration_iics_export'
  | 'entity_icons'

const GENERIC_MIME_TYPES = new Set([
  '',
  'application/octet-stream',
  'binary/octet-stream',
])

const STRICT_EXTENSION_MIME_MAP: Record<string, string> = {
  md: 'text/markdown',
  markdown: 'text/markdown',
}

const EXTENSION_MIME_MAP: Record<string, string> = {
  txt: 'text/plain',
  csv: 'text/csv',
  json: 'application/json',
  html: 'text/html',
  htm: 'text/html',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  heic: 'image/heic',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  webm: 'video/webm',
  ogg: 'audio/ogg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  flac: 'audio/flac',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
  m4v: 'video/x-m4v',
  ogv: 'video/ogg',
  sql: 'application/sql',
  py: 'text/x-python',
  pyspark: 'text/x-python',
  scala: 'text/x-scala',
  sh: 'application/x-sh',
  yaml: 'application/x-yaml',
  yml: 'application/x-yaml',
  r: 'text/x-r',
  ipynb: 'application/json',
  jsonl: 'application/jsonl',
  ndjson: 'application/ndjson',
  cypher: 'application/x-cypher-query',
  sqlite: 'application/vnd.sqlite3',
  sqlite3: 'application/vnd.sqlite3',
}

const getFileExtension = (filename: string): string => {
  const dotIndex = filename.lastIndexOf('.')
  if (dotIndex === -1 || dotIndex === filename.length - 1) return ''
  return filename.slice(dotIndex + 1).toLowerCase()
}

/** Control chars and other bytes that break multipart / storage metadata. */
// eslint-disable-next-line no-control-regex
const UPLOAD_FILENAME_INVALID_CHARS = /[\x00-\x1f\x7f]/

export interface UploadFilenameValidation {
  ok: boolean
  reason?: string
}

/**
 * Validate a filename before upload. Unicode and emoji are allowed; control chars and
 * path traversal segments are rejected with a user-facing reason.
 */
export function validateUploadFilename(filename: string): UploadFilenameValidation {
  const trimmed = (filename || '').trim()
  if (!trimmed) {
    return { ok: false, reason: 'Filename is empty' }
  }
  if (safeRegexTest(UPLOAD_FILENAME_INVALID_CHARS, trimmed)) {
    return {
      ok: false,
      reason: 'Filename contains invalid control characters. Rename the file and try again.',
    }
  }
  const normalized = trimmed.replace(/\\/g, '/')
  if (normalized.split('/').some((seg) => seg === '..' || seg.includes('..'))) {
    return {
      ok: false,
      reason: 'Filename contains invalid path characters. Use a simple name without ".." segments.',
    }
  }
  return { ok: true }
}

export interface UploadFilePartitionResult {
  valid: File[]
  rejected: Array<{ file: File; reason: string }>
}

/** Shown for 0-byte uploads; keep in sync with backend EMPTY_FILE_MESSAGE. */
export const EMPTY_UPLOAD_FILE_REASON = 'The file content is corrupted or empty'

/** Agent-chat warning when a 0-byte file is rejected before upload. */
export const EMPTY_CHAT_FILE_WARNING = 'Upload failed – file is empty'

/** Keep in sync with backend FileService.MAX_FILE_SIZE (512 MiB). */
export const MAX_UPLOAD_FILE_BYTES = 512 * 1024 * 1024
export const MAX_UPLOAD_FILE_SIZE_MB = 512

/** Keep in sync with Agentserve DOCUMENT_MAX_FILE_SIZE_MB. Chat reads fail above this. */
export const CHAT_DOCUMENT_MAX_BYTES = 200 * 1024 * 1024
export const CHAT_DOCUMENT_MAX_MB = 200

export function chatDocumentOversizeReason(filename: string): string {
  return `File ${filename} exceeds the ${CHAT_DOCUMENT_MAX_MB} MB limit for chat documents.`
}

/** Shown for oversized File Reader / platform uploads; keep in sync with backend. */
export function oversizeUploadFileReason(filename: string): string {
  return `File ${filename} exceeds the maximum allowed size limit of ${MAX_UPLOAD_FILE_SIZE_MB} MB`
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function formatPerFileUploadReasons(errors: unknown): string {
  if (!Array.isArray(errors)) return ''
  const parts: string[] = []
  for (const item of errors) {
    if (!isPlainObject(item)) continue
    const filename = typeof item.filename === 'string' ? item.filename.trim() : ''
    const reason = typeof item.detail === 'string' ? item.detail.trim() : ''
    if (filename && reason) {
      parts.push(`${filename}: ${reason}`)
    } else if (reason) {
      parts.push(reason)
    } else if (filename) {
      parts.push(filename)
    }
  }
  return parts.join('; ')
}

function readLegacyUploadDetail(detail: unknown): string {
  if (typeof detail === 'string') return detail.trim()
  if (!isPlainObject(detail)) return ''
  const perFile = formatPerFileUploadReasons(detail.errors)
  if (perFile) return perFile
  return typeof detail.message === 'string' ? detail.message.trim() : ''
}

/**
 * Parse an upload error body. Prefers per-file envelope reasons, then the
 * general envelope message, then legacy `detail`, then the HTTP status.
 */
export function readUploadFailureMessage(errorData: unknown, status: number): string {
  const statusFallback = `Upload failed: ${status}`
  if (!isPlainObject(errorData)) return statusFallback

  const nested = isPlainObject(errorData.error) ? errorData.error : null
  const envelopeErrors = nested ? formatPerFileUploadReasons(nested.errors) : ''
  if (envelopeErrors) return envelopeErrors

  if (nested && typeof nested.message === 'string' && nested.message.trim()) {
    return nested.message.trim()
  }
  if (typeof errorData.error === 'string' && errorData.error.trim()) {
    return errorData.error.trim()
  }

  const legacy = readLegacyUploadDetail(errorData.detail)
  if (legacy) return legacy

  return statusFallback
}

async function throwIfUploadNotOk(response: Response): Promise<void> {
  if (response.ok) return
  const errorData: unknown = await response.json().catch(() => ({}))
  const errorMessage = readUploadFailureMessage(errorData, response.status)
  logger.error('File upload failed', { status: response.status, error: errorMessage })
  throw new Error(errorMessage)
}

/** Split files into uploadable vs rejected based on type support, filename, and size. */
export function partitionFilesForUpload(files: File[] | FileList): UploadFilePartitionResult {
  const valid: File[] = []
  const rejected: Array<{ file: File; reason: string }> = []

  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    const nameCheck = validateUploadFilename(file.name)
    if (!nameCheck.ok) {
      rejected.push({ file, reason: nameCheck.reason ?? 'Invalid filename' })
      continue
    }
    if (!isFileTypeSupported(file)) {
      rejected.push({
        file,
        reason: `Unsupported file type: ${file.type || getFileExtension(file.name) || 'unknown'}`,
      })
      continue
    }
    if (file.size === 0) {
      rejected.push({ file, reason: EMPTY_UPLOAD_FILE_REASON })
      continue
    }
    if (file.size > MAX_UPLOAD_FILE_BYTES) {
      rejected.push({ file, reason: oversizeUploadFileReason(file.name) })
      continue
    }
    valid.push(file)
  }

  return { valid, rejected }
}

function appendUploadFormFields(
  formData: FormData,
  context: StorageContext,
  sessionId: string,
  destination?: {
    agentId?: string
    registeredStorageFolderId?: string
    projectId?: string
  },
): void {
  // Non-file fields first so batch metadata is parsed even if a file part is malformed.
  formData.append('context', context)
  formData.append('session_id', sessionId)
  const agentId = destination?.agentId?.trim()
  const folderId = destination?.registeredStorageFolderId?.trim()
  const projectId = destination?.projectId?.trim()
  if (agentId) formData.append('agent_id', agentId)
  if (folderId) formData.append('registered_storage_folder_id', folderId)
  if (projectId) formData.append('project_id', projectId)
}

/**
 * Resolve MIME type for a file using strict extension overrides and safe fallback rules.
 * For `.md`/`.markdown`, this always returns `text/markdown`.
 */
export function resolveFileMimeType(file: Pick<File, 'name' | 'type'>): string {
  const extension = getFileExtension(file.name)
  if (!extension) return file.type || ''

  if (STRICT_EXTENSION_MIME_MAP[extension]) {
    return STRICT_EXTENSION_MIME_MAP[extension]
  }

  const currentType = (file.type || '').toLowerCase()
  if (!GENERIC_MIME_TYPES.has(currentType)) {
    return file.type
  }

  return EXTENSION_MIME_MAP[extension] || file.type || ''
}

/**
 * Normalize browser-provided MIME type when it is generic/incorrect.
 * Returns the original file if no normalization is required.
 */
export function normalizeFileForUpload(file: File): File {
  const resolvedMimeType = resolveFileMimeType(file)
  if (!resolvedMimeType || resolvedMimeType === file.type) return file

  return new File([file], file.name, {
    type: resolvedMimeType,
    lastModified: file.lastModified,
  })
}

/**
 * Upload a file to the platform's storage
 *
 * @param file - The file to upload
 * @param context - Storage context ('prompt_chat', 'model_chat', 'agent_playground')
 * @param sessionId - Session ID for organizing files
 * @returns UploadedFile with file UUID and URL
 */
export async function uploadFile(
  file: File,
  context: StorageContext,
  sessionId: string,
  options?: { agentId?: string },
): Promise<UploadedFile> {
  const formData = new FormData()
  appendUploadFormFields(formData, context, sessionId, { agentId: options?.agentId })
  const normalizedFile = normalizeFileForUpload(file)
  if (normalizedFile.size === 0) {
    throw new Error(EMPTY_CHAT_FILE_WARNING)
  }
  formData.append('file', normalizedFile, normalizedFile.name)

  const response = await fileApiFetch(`${config.api.baseUrl}/files/upload`, {
    method: 'POST',
    headers: fileAuthHeaders(),
    body: formData,
  })

  await throwIfUploadNotOk(response)

  const result = await response.json()
  logger.debug('File uploaded successfully', {
    file_id: result.file_id,
    size: result.size_bytes,
  })

  return result
}

export interface BatchUploadResult {
  uploaded: UploadedFile[]
  errors: { filename: string; detail: string }[]
}

export interface BatchUploadOptions {
  preserveFolderStructure?: boolean
  /** Abort the in-flight multipart request (Cancel in File Reader). */
  signal?: AbortSignal
  /** Upload-body percent 0–100. Uses XHR so the request stays abortable. */
  onProgress?: (percent: number) => void
  /** Workflow registered folder. Omit to keep tenant platform storage. */
  registeredStorageFolderId?: string
  projectId?: string
}

/**
 * Max files per `upload-batch` HTTP request. Keeps each call under typical
 * gateway idle/read timeouts when storage PUTs are remote (S3/Azure/GCS).
 */
export const BATCH_UPLOAD_CHUNK_MAX_FILES = 8

/** Soft byte cap per chunk. A single larger file is still sent as its own chunk. */
export const BATCH_UPLOAD_CHUNK_MAX_BYTES = 32 * 1024 * 1024

/** Split a folder upload into gateway-safe `upload-batch` chunks (same session_id). */
export function splitFilesIntoUploadChunks(files: File[]): File[][] {
  const chunks: File[][] = []
  let current: File[] = []
  let currentBytes = 0
  for (const file of files) {
    const wouldExceedCount = current.length >= BATCH_UPLOAD_CHUNK_MAX_FILES
    const wouldExceedBytes =
      current.length > 0 && currentBytes + file.size > BATCH_UPLOAD_CHUNK_MAX_BYTES
    if (wouldExceedCount || wouldExceedBytes) {
      chunks.push(current)
      current = []
      currentBytes = 0
    }
    current.push(file)
    currentBytes += file.size
  }
  if (current.length > 0) {
    chunks.push(current)
  }
  return chunks
}

function totalFileBytes(files: File[]): number {
  return files.reduce((sum, file) => sum + file.size, 0)
}

function mergeBatchUploadResults(parts: BatchUploadResult[]): BatchUploadResult {
  const uploaded: UploadedFile[] = []
  const errors: { filename: string; detail: string }[] = []
  for (const part of parts) {
    uploaded.push(...part.uploaded)
    errors.push(...part.errors)
  }
  return { uploaded, errors }
}

const ABORT_ERROR_NAME = 'AbortError'

/** True when the user (or an unmount) aborted an upload; do not treat as a failure. */
export function isUploadAbortError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false
  return 'name' in err && err.name === ABORT_ERROR_NAME
}

function createAbortError(): DOMException {
  return new DOMException('The operation was aborted.', ABORT_ERROR_NAME)
}

function clampUploadPercent(loaded: number, total: number): number {
  if (!Number.isFinite(loaded) || !Number.isFinite(total) || total <= 0) return 0
  return Math.min(100, Math.max(0, Math.round((loaded / total) * 100)))
}

function buildBatchUploadFormData(
  files: File[] | FileList,
  context: StorageContext,
  sessionId: string,
  preserveFolderStructure?: boolean,
  destination?: { registeredStorageFolderId?: string; projectId?: string },
): FormData {
  const formData = new FormData()
  appendUploadFormFields(formData, context, sessionId, destination)
  for (let i = 0; i < files.length; i++) {
    const f = files[i] as File & { webkitRelativePath?: string }
    const normalized = normalizeFileForUpload(f)
    const uploadFilename =
      preserveFolderStructure && f.webkitRelativePath
        ? f.webkitRelativePath
        : normalized.name
    formData.append('files', normalized, uploadFilename)
  }
  return formData
}

/**
 * XHR upload so we can report `xhr.upload` progress and abort via AbortSignal.
 * URL is sanitized the same way as `fileApiFetch` (CWE-918).
 */
function uploadFormDataWithProgress(
  url: string,
  formData: FormData,
  onProgress: (percent: number) => void,
  signal?: AbortSignal,
): Promise<Response> {
  const sanitizedUrl = sanitizeFetchUrl(url, config.api.baseUrl)

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError())
      return
    }

    const xhr = new XMLHttpRequest()
    const onSignalAbort = () => {
      xhr.abort()
    }
    const cleanup = () => {
      signal?.removeEventListener('abort', onSignalAbort)
    }

    xhr.upload.addEventListener('progress', (event: ProgressEvent) => {
      if (!event.lengthComputable) return
      onProgress(clampUploadPercent(event.loaded, event.total))
    })
    xhr.addEventListener('load', () => {
      cleanup()
      resolve(new Response(xhr.responseText, { status: xhr.status, statusText: xhr.statusText }))
    })
    xhr.addEventListener('error', () => {
      cleanup()
      reject(new Error('Network error during upload'))
    })
    xhr.addEventListener('abort', () => {
      cleanup()
      reject(createAbortError())
    })

    signal?.addEventListener('abort', onSignalAbort)
    xhr.open('POST', sanitizedUrl)
    applyFileAuthHeaders(xhr)
    xhr.send(formData)
  })
}

async function uploadOneBatchRequest(
  files: File[] | FileList,
  context: StorageContext,
  sessionId: string,
  options?: BatchUploadOptions,
): Promise<BatchUploadResult> {
  const formData = buildBatchUploadFormData(
    files,
    context,
    sessionId,
    options?.preserveFolderStructure,
    {
      registeredStorageFolderId: options?.registeredStorageFolderId,
      projectId: options?.projectId,
    },
  )
  const url = `${config.api.baseUrl}/files/upload-batch`

  const response = options?.onProgress
    ? await uploadFormDataWithProgress(
        url,
        formData,
        options.onProgress,
        options.signal,
      )
    : await fileApiFetch(url, {
        method: 'POST',
        headers: fileAuthHeaders(),
        body: formData,
        signal: options?.signal,
      })

  await throwIfUploadNotOk(response)

  const result: BatchUploadResult = await response.json()
  logger.debug('Batch file upload chunk completed', {
    uploaded: result.uploaded.length,
    errors: result.errors.length,
  })
  return result
}

function chunkFailureError(
  chunk: File[],
  err: unknown,
): { filename: string; detail: string } {
  const filename = chunk[0]?.name || 'batch'
  const detail = err instanceof Error ? err.message : 'Upload chunk failed'
  return { filename, detail }
}

function overallUploadPercent(
  completedBytes: number,
  chunkBytes: number,
  chunkPercent: number,
  totalBytes: number,
): number {
  const loaded = completedBytes + (chunkPercent / 100) * chunkBytes
  return clampUploadPercent(loaded, totalBytes)
}

/**
 * Upload multiple files to the platform's storage.
 *
 * Large folders are split into smaller `upload-batch` requests that share
 * `sessionId` so each HTTP call can finish under gateway timeout.
 *
 * @param files - Array or FileList of files to upload
 * @param context - Storage context
 * @param sessionId - Session / batch ID for organizing files
 * @returns BatchUploadResult with per-file successes and errors
 */
export async function uploadFiles(
  files: File[] | FileList,
  context: StorageContext,
  sessionId: string,
  options?: BatchUploadOptions,
): Promise<BatchUploadResult> {
  const fileList = Array.from(files)
  const chunks = splitFilesIntoUploadChunks(fileList)
  if (chunks.length <= 1) {
    return uploadOneBatchRequest(files, context, sessionId, options)
  }

  const totalBytes = Math.max(1, totalFileBytes(fileList))
  let completedBytes = 0
  const parts: BatchUploadResult[] = []

  for (let i = 0; i < chunks.length; i++) {
    if (options?.signal?.aborted) {
      throw createAbortError()
    }
    const chunk = chunks[i]
    const chunkBytes = totalFileBytes(chunk)
    try {
      const onProgress = options?.onProgress
        ? (percent: number) => {
            options.onProgress?.(
              overallUploadPercent(completedBytes, chunkBytes, percent, totalBytes),
            )
          }
        : undefined
      const chunkResult = await uploadOneBatchRequest(chunk, context, sessionId, {
        ...options,
        onProgress,
      })
      parts.push(chunkResult)
      completedBytes += chunkBytes
    } catch (err) {
      if (isUploadAbortError(err)) {
        throw err
      }
      const merged = mergeBatchUploadResults(parts)
      if (merged.uploaded.length === 0) {
        throw err
      }
      merged.errors.push(chunkFailureError(chunk, err))
      logger.error(
        'Batch upload chunk failed after partial success',
        err instanceof Error ? err : undefined,
        { chunkIndex: i, chunkCount: chunks.length, uploaded: merged.uploaded.length },
      )
      return merged
    }
  }

  const result = mergeBatchUploadResults(parts)
  logger.debug('Batch file upload completed', {
    uploaded: result.uploaded.length,
    errors: result.errors.length,
    chunks: chunks.length,
  })
  return result
}

/**
 * Get the full URL for a file by UUID
 *
 * @param fileId - File UUID
 * @returns Full URL for accessing the file
 */
export function getFileUrl(fileId: string): string {
  // If already a full URL, return as-is
  if (fileId.startsWith('http://') || fileId.startsWith('https://')) {
    return fileId
  }

  // If it's a data URL, return as-is
  if (fileId.startsWith('data:')) {
    return fileId
  }

  // If it's an API path, prepend the base URL
  if (fileId.startsWith('/api/')) {
    return `${config.api.baseUrl.replace('/api', '')}${fileId}`
  }

  // Construct the file URL using UUID
  return `${config.api.baseUrl}/files/${fileId}`
}

/**
 * Get file information by UUID
 *
 * @param fileId - File UUID
 * @returns FileInfo with file details
 */
export async function getFileInfo(fileId: string): Promise<FileInfo> {
  const response = await fileApiFetch(`${config.api.baseUrl}/files/${fileId}/info`, {
    headers: fileAuthHeaders(),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData?.detail || `Failed to get file info: ${response.status}`)
  }

  return response.json()
}

/**
 * List files for a context and session (e.g. workflow_ingestion batch for AI Workflows).
 *
 * @param context - Storage context (e.g. 'workflow_ingestion')
 * @param sessionId - Batch/session ID
 * @returns List of FileInfo
 */
export async function listFiles(
  context: StorageContext,
  sessionId: string
): Promise<FileInfo[]> {
  const params = new URLSearchParams({ context, session_id: sessionId })

  const response = await fileApiFetch(`${config.api.baseUrl}/files?${params}`, {
    headers: fileAuthHeaders(),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData?.detail || `Failed to list files: ${response.status}`)
  }

  const data = await response.json()
  return data.files ?? []
}

/** Upload a .zip into a migration batch (ETL/catalog extract; shell_cron stores ZIP intact). */
export async function uploadZipBatch(
  file: File,
  context: StorageContext,
  sessionId: string,
): Promise<BatchUploadResult> {
  const formData = new FormData()
  appendUploadFormFields(formData, context, sessionId)
  formData.append('file', file, file.name)

  const response = await fileApiFetch(`${config.api.baseUrl}/files/upload-zip-batch`, {
    method: 'POST',
    headers: fileAuthHeaders(),
    body: formData,
  })

  await throwIfUploadNotOk(response)

  return response.json()
}

/**
 * Get file as base64 encoded string
 *
 * @param fileId - File UUID
 * @returns Object with base64 data and content type
 */
export async function getFileBase64(fileId: string): Promise<{
  file_id: string
  content_type: string
  base64: string
}> {
  const response = await fileApiFetch(`${config.api.baseUrl}/files/${fileId}/base64`, {
    headers: fileAuthHeaders(),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData?.detail || `Failed to get file: ${response.status}`)
  }

  return response.json()
}

/**
 * Delete a file by UUID
 *
 * @param fileId - File UUID
 */
export async function deleteFile(fileId: string): Promise<void> {
  const response = await fileApiFetch(`${config.api.baseUrl}/files/${fileId}`, {
    method: 'DELETE',
    headers: fileAuthHeaders(),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData?.detail || `Failed to delete file: ${response.status}`)
  }
}

/**
 * Download a file by UUID (authenticated blob → browser save).
 * Used by File Reader local-upload list so authors can inspect source files.
 *
 * @param fileId - File UUID
 * @param filename - Preferred download filename (falls back to Content-Disposition / fileId)
 */
export async function downloadFile(fileId: string, filename?: string): Promise<void> {
  const response = await fileApiFetch(`${config.api.baseUrl}/files/${fileId}`, {
    headers: fileAuthHeaders(),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const detail = errorData?.detail
    const message =
      typeof detail === 'string'
        ? detail
        : detail?.message ?? `Download failed: ${response.status}`
    throw new Error(message)
  }

  const blob = await response.blob()
  let resolvedName = (filename || '').trim() || 'download'
  const disposition = response.headers.get('Content-Disposition')
  if (disposition) {
    const utfMatch = disposition.match(/filename\*=UTF-8''([^;\s]+)/i)
    const plainMatch = disposition.match(/filename="?([^";\n]+)"?/i)
    const raw = utfMatch?.[1] || plainMatch?.[1]
    if (raw) {
      try {
        resolvedName = decodeURIComponent(raw.replace(/["']/g, '').trim()) || resolvedName
      } catch {
        resolvedName = raw.replace(/["']/g, '').trim() || resolvedName
      }
    }
  }

  const url = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = resolvedName
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
}

const DANGEROUS_ETL_EXTENSIONS = new Set([
  'exe', 'dll', 'so', 'dylib', 'bat', 'cmd', 'msi', 'scr', 'com', 'pif',
])

export function isDangerousEtlFilename(filename: string): boolean {
  const ext = getFileExtension(filename)
  return DANGEROUS_ETL_EXTENSIONS.has(ext)
}

/**
 * Permissive client-side gate for Migration Studio ETL uploads.
 * Server performs authoritative UTF-8 / extension validation.
 */
export function isMigrationEtlFileAllowed(file: Pick<File, 'name' | 'type'>): boolean {
  if (isDangerousEtlFilename(file.name)) return false

  const ext = getFileExtension(file.name)
  const etlExtensions = new Set([
    'py', 'pyspark', 'sql', 'ddl', 'proc', 'prc', 'plsql',
    // Oracle SQL Developer / Toad style PL/SQL unit extensions
    'pck', 'pkg', 'pks', 'pkb', 'fnc', 'trg', 'vw', 'tps', 'tpb',
    'scala', 'r', 'ipynb',
    'xml', 'dtsx', 'cbl', 'cob', 'yaml', 'yml', 'json', 'md', 'txt', 'sh', 'java', 'js', 'ts',
    'go', 'rb', 'php', 'cs', 'kt', 'gradle', 'properties', 'csv', 'html', 'htm', 'conf', 'cfg',
    'ini', 'env', 'tf', 'hql', 'pig', 'groovy', 'ps1',
    // Informatica IICS offline export (Oracle→BigQuery Discovery via Legacy ETL Local Upload)
    'taskflow', 'mct', 'prm',
  ])
  if (etlExtensions.has(ext)) return true

  const resolvedMimeType = resolveFileMimeType(file).toLowerCase()
  if (resolvedMimeType.startsWith('text/')) return true
  if (GENERIC_MIME_TYPES.has(resolvedMimeType)) return true

  return isFileTypeSupported(file as File)
}

/**
 * Check if a file type is supported for upload
 *
 * @param file - The file to check
 * @returns true if the file type is supported
 */
export function isFileTypeSupported(file: File): boolean {
  const supportedTypes = new Set([
    // Images
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'image/bmp',
    'image/tiff',
    'image/avif',
    'image/heic',
    // Audio
    'audio/mpeg',
    'audio/mp4',
    'audio/x-m4a',
    'audio/wav',
    'audio/webm',
    'audio/ogg',
    'audio/aac',
    'audio/flac',
    // Video
    'video/mp4',
    'video/quicktime',
    'video/x-msvideo',
    'video/x-matroska',
    'video/x-m4v',
    'video/webm',
    'video/ogg',
    // Documents
    'application/pdf',
    'text/plain',
    'text/csv',
    'application/json',
    'text/html',
    'text/markdown',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-powerpoint',
    'application/x-yaml',
    'application/yaml',
    'text/yaml',
    'text/x-yaml',
    "application/sql",
    "text/x-sql",
    "application/x-sql",
    "text/sql",
    "application/jsonl",
    "application/x-jsonlines",
    "application/ndjson",
    "application/x-cypher-query",
    "text/x-cypher-query",
    "text/x-cypher",
    "application/vnd.sqlite3",
    "application/x-sqlite3",
    "text/x-python",
    "application/x-python-code",
    "text/x-scala",
    "text/x-sh",
    "text/x-r",
    "application/zip",
    "application/x-zip-compressed",
  ])

  const resolvedMimeType = resolveFileMimeType(file)
  if (supportedTypes.has(resolvedMimeType)) return true

  // Browsers often report empty/wrong MIME for .md, .csv, .txt etc — fall back to extension check
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  const supportedExtensions = new Set([
    'md', 'markdown', 'txt', 'csv', 'html', 'htm',
    'pdf', 'docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt',
    'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tif', 'tiff', 'avif', 'heic',
    'mp3', 'wav', 'webm', 'ogg', 'm4a', 'aac', 'flac',
    'mp4', 'mov', 'avi', 'mkv', 'm4v', 'ogv',
    'sql', 'jsonl', 'ndjson', 'cypher', 'sqlite', 'sqlite3',
    'py', 'pyspark', 'ddl', 'proc', 'prc', 'plsql', 'scala', 'r', 'ipynb',
    'xml', 'dtsx', 'cbl', 'cob', 'yaml', 'yml', 'sh', 'java', 'js', 'ts', 'go', 'rb', 'php', 'cs', 'kt', 'zip',
  ])
  return supportedExtensions.has(ext)
}

/**
 * Get a human-readable file size
 *
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`
}

/**
 * Get file type category from MIME type
 *
 * @param mimeType - MIME type string
 * @returns File type category
 */
export function getFileTypeCategory(mimeType: string): 'image' | 'video' | 'audio' | 'document' {
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType.startsWith('audio/')) return 'audio'
  return 'document'
}

export const fileApi = {
  upload: uploadFile,
  uploadBatch: uploadFiles,
  uploadZipBatch,
  list: listFiles,
  getUrl: getFileUrl,
  getInfo: getFileInfo,
  getBase64: getFileBase64,
  download: downloadFile,
  delete: deleteFile,
  isTypeSupported: isFileTypeSupported,
  isMigrationEtlFileAllowed,
  isDangerousEtlFilename,
  isUploadAbortError,
  validateFilename: validateUploadFilename,
  partitionForUpload: partitionFilesForUpload,
  oversizeReason: oversizeUploadFileReason,
  formatSize: formatFileSize,
  getTypeCategory: getFileTypeCategory,
}
